import {
    Box,
    TableCell,
    TableRow,
    Theme,
    Typography
} from "@mui/material";
import {
    useEffect,
    useState
} from "react";
import {
    Order,
    PaginationObj,
    SortingObj,
    TableHeaderData
} from "../../../../investorOverview/types";
import CustomTable from "../../../../investorOverview/common/customTable";
import { formatCurrency } from "../../../../../utils/utilityFunctions";
import {
    FetchTableDataProps,
    SchemewiseAnalysisProps,
    SchemewiseAnalysisTableData
} from "../types";
import {
    GetSchemeDetailsResponseBody,
    SchemeDetailsData
} from "../../../../../redux-store/types/analytics";
import {
    useDispatch,
    useSelector
} from "react-redux";
import { RootStateType } from "../../../../../redux-store/reducers";
import { getSchemeDetails } from "../../../../../redux-store/actions/analytics";
import { SchemewiseAnalysisLoading } from "../../../loadingComponents";
import { NO_DATA_AVAILABLE } from "../../../../../utils/constant";

const tableHeaderData: TableHeaderData[] = [
    {
        id: 'schemeName',
        label: 'SCHEME',
        align: 'left',
        isSortable: false,
    },
    {
        id: 'investedValue',
        label: 'INVESTED AMOUNT',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'marketValue',
        label: 'MARKET VALUE',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'unrealisedAmount',
        label: 'GAIN',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'exposure',
        label: 'Exposure',
        align: 'center',
        isSortable: true,
    }
]

const assetGroupMap: { [key: string]: string; } = {
    "Mutual Fund": "MF"
}

function TableBodyData({ data }: {
    data: SchemeDetailsData[]
}): JSX.Element {
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    return (
        <>
            {data.map((obj: SchemeDetailsData, index: number) => (
                <TableRow
                    key={`${obj?.schemeName ?? ""}-${index}`}
                    sx={(theme: Theme) => ({
                        '& td': {
                            paddingTop: "15px",
                            paddingBottom: "15px",
                            borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                        },
                        '& td:first-of-type': {
                            paddingLeft: "0px"
                        },
                    })}
                >
                    <TableCell align="left">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary,
                                '&:hover': {
                                    color: theme.palette.primary[500]
                                },
                                // textTransform: 'capitalize'
                            })}
                        >
                            {obj?.schemeName ?? "-"}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary,
                            })}
                        >
                            {(obj?.investedValue ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(obj?.investedValue ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: obj?.investedValue ?? 0, locale, currencyCode })}`}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary,
                            })}
                        >
                            {(obj?.marketValue ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(obj?.marketValue ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: obj?.marketValue ?? 0, locale, currencyCode })}`}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary,
                            })}
                        >
                            {(obj?.percentage?.amount ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(obj?.percentage?.amount ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: obj?.percentage?.amount ?? 0, locale, currencyCode })}`}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary
                            })}
                        >
                            {!Number.isNaN(Number(obj?.exposure ?? 0)) ?
                                Math.round((obj?.exposure ?? 0) * 100) / 100 : '0'}%
                        </Typography>
                    </TableCell>
                </TableRow>
            ))}
        </>

    )
}

// Custom Mobile Responsive Body Component for Schemewise Analysis
// function MobileResponsiveBody({ selectedHeaderKey, data }: { selectedHeaderKey: string, data: SchemeDetailsData[] }): JSX.Element {
//     const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

//     const getFormattedValue = (item: SchemeDetailsData, key: string) => {
//         switch (key) {
//             case 'investedValue':
//                 return (item?.investedValue ?? 0) < 0 ?
//                     `- ${formatCurrency({ value: Math.abs(item?.investedValue ?? 0), locale, currencyCode })}` :
//                     `${formatCurrency({ value: item?.investedValue ?? 0, locale, currencyCode })}`;
//             case 'marketValue':
//                 return (item?.marketValue ?? 0) < 0 ?
//                     `- ${formatCurrency({ value: Math.abs(item?.marketValue ?? 0), locale, currencyCode })}` :
//                     `${formatCurrency({ value: item?.marketValue ?? 0, locale, currencyCode })}`;
//             case 'unrealisedAmount':
//                 return (item?.percentage?.amount ?? 0) < 0 ?
//                     `- ${formatCurrency({ value: Math.abs(item?.percentage?.amount ?? 0), locale, currencyCode })}` :
//                     `${formatCurrency({ value: item?.percentage?.amount ?? 0, locale, currencyCode })}`;
//             case 'exposure':
//                 return `${!Number.isNaN(Number(item?.exposure ?? 0)) ? Math.round((item?.exposure ?? 0) * 100) / 100 : '0'}%`;
//             default:
//                 return item?.[key as keyof SchemeDetailsData] ?? "-";
//         }
//     };

//     return (
//         <>
//             {data.map((item: SchemeDetailsData, index: number) => (
//                 <TableRow key={`${item?.schemeName ?? ""}-${index}`}>
//                     <TableCell align="left">
//                         <Typography
//                             variant="body_500"
//                             sx={(theme: Theme) => ({
//                                 lineHeight: "20px",
//                                 letterSpacing: "0.02px",
//                                 color: theme.palette.text.primary,
//                                 '&:hover': {
//                                     color: theme.palette.primary[500]
//                                 },
//                             })}
//                         >
//                             {item?.schemeName ?? "-"}
//                         </Typography>
//                     </TableCell>
//                     <TableCell align="center">
//                         <Typography
//                             variant="body_500"
//                             sx={(theme: Theme) => ({
//                                 lineHeight: "20px",
//                                 letterSpacing: "0.02px",
//                                 color: theme.palette.text.primary,
//                             })}
//                         >
//                             {getFormattedValue(item, selectedHeaderKey)}
//                         </Typography>
//                     </TableCell>
//                 </TableRow>
//             ))}
//         </>
//     );
// }
function MobileResponsiveBodyScheme({
    data
}: {
    data: SchemeDetailsData[]
}): JSX.Element {
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const getFormattedValue = (item: SchemeDetailsData, key: string) => {
        switch (key) {
            case 'investedValue':
                return (item?.investedValue ?? 0) < 0
                    ? `- ${formatCurrency({ value: Math.abs(item?.investedValue ?? 0), locale, currencyCode })}`
                    : `${formatCurrency({ value: item?.investedValue ?? 0, locale, currencyCode })}`;
            case 'marketValue':
                return (item?.marketValue ?? 0) < 0
                    ? `- ${formatCurrency({ value: Math.abs(item?.marketValue ?? 0), locale, currencyCode })}`
                    : `${formatCurrency({ value: item?.marketValue ?? 0, locale, currencyCode })}`;
            case 'unrealisedAmount':
                return (item?.percentage?.amount ?? 0) < 0
                    ? `- ${formatCurrency({ value: Math.abs(item?.percentage?.amount ?? 0), locale, currencyCode })}`
                    : `${formatCurrency({ value: item?.percentage?.amount ?? 0, locale, currencyCode })}`;
            case 'exposure':
                return `${!Number.isNaN(Number(item?.exposure ?? 0)) ? Math.round((item?.exposure ?? 0) * 100) / 100 : '0'}%`;
            default:
                return item?.[key as keyof SchemeDetailsData] ?? "-";
        }
    };

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {data.map((item, index) => (
                <Box
                    key={`${item?.schemeName ?? ""}-${index}`}
                    sx={(theme: Theme) => ({
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: "12px",
                        padding: "16px",
                        backgroundColor: theme.palette.background.paper,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    })}
                >
                    {/* Scheme Name */}
                    <Typography
                        variant="body_600"
                        sx={(theme: Theme) => ({
                            color: theme.palette.text.primary,
                            fontSize: "16px",
                            marginBottom: "8px",
                        })}
                    >
                        {item?.schemeName ?? "-"}
                    </Typography>

                    {/* Scheme Details */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                        }}
                    >
                        {tableHeaderData
                            .filter(header => header.id !== "schemeName") 
                            .map(header => (
                                <Box 
                                    key={header.id}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography
                                        variant="overline"
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.text.secondary,
                                            fontSize: "12px",
                                            lineHeight: "16px",
                                            textAlign: "left"
                                        })}
                                    >
                                        {header.label}
                                    </Typography>
                                    <Typography
                                        variant="body_500"
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.text.primary,
                                            fontSize: "14px",
                                            lineHeight: "20px",
                                            textAlign: "right"
                                        })}
                                    >
                                        {getFormattedValue(item, header.id)}
                                    </Typography>
                                </Box>
                            ))}
                    </Box>
                </Box>
            ))}
        </Box>
    );
}


export default function SchemewiseAnalysis({
    clientCode,
    portfolioId,
    assetGroup
}: SchemewiseAnalysisProps): JSX.Element {
    const dispatch = useDispatch();

    const [isLoading, setLoading] = useState<boolean>(false)
    const [tableData, setTableData] = useState<SchemewiseAnalysisTableData>({
        data: [],
        isSuccess: true,
        message: ""
    })
    const [tableSortingObj, setTableSortingObj] = useState<SortingObj>({
        order: "asc",
        orderBy: "investedValue"
    })
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0
    })

    const { iwappRmId = "" } = useSelector((state: RootStateType) => state.id);

    const fetchData = async ({
        sortItem = "investedValue",
        order = "asc",
        currentPage = 1,
        rowsPerPage = 5,
        clientCode,
        portfolioId,
        selectedAssetGroup = "Mutual Fund"
    }: FetchTableDataProps) => {
        try {
            setLoading(true)

            const payload = {
                rmId: `${iwappRmId}`,
                clientCode,
                portfolioId,
                sortItem,
                order,
                currentPage,
                rowsPerPage,
                assetGroup: assetGroupMap[selectedAssetGroup],
            }

            const { result, message, isSuccess, totalLength = 0 } = await (dispatch(getSchemeDetails(payload)) as unknown as Promise<GetSchemeDetailsResponseBody>)

            if (isSuccess) {
                setTableData({
                    isSuccess,
                    message,
                    data: result ?? []
                })
                setTablePaginationObj(prevState => ({
                    ...prevState,
                    totalRows: totalLength
                }))
            } else {
                setTableData({
                    data: [],
                    isSuccess: false,
                    message: ""
                })
                setTablePaginationObj({
                    currentPage: 0,
                    rowsPerPage: 5,
                    totalRows: 0
                })
            }

        } catch (error) {
            console.log((error as Error)?.message);
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData({
            clientCode,
            portfolioId,
            selectedAssetGroup: assetGroup
        })
    }, [
        clientCode,
        portfolioId,
        assetGroup
    ])

    const handleSort = (orderBy: string, order: Order) => {
        setTableSortingObj({
            order,
            orderBy
        })

        fetchData({
            sortItem: orderBy,
            order,
            currentPage: tablePaginationObj.currentPage + 1,
            rowsPerPage: tablePaginationObj.rowsPerPage,
            clientCode,
            portfolioId
        })
    }

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            rowsPerPage: Number(event.target.value),
            currentPage: 0,
        }))

        fetchData({
            rowsPerPage: Number(event.target.value),
            sortItem: tableSortingObj.orderBy,
            order: tableSortingObj.order,
            clientCode,
            portfolioId
        })
    }

    const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: page
        }))

        fetchData({
            currentPage: page + 1,
            sortItem: tableSortingObj.orderBy,
            order: tableSortingObj.order,
            rowsPerPage: tablePaginationObj.rowsPerPage,
            clientCode,
            portfolioId
        })
    }

    if (isLoading) {
        return (<SchemewiseAnalysisLoading />)
    }

    if (!isLoading && !tableData?.isSuccess) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        paddingBottom: "20px"
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: '25px',
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                        })}
                    >
                        Schemewise Analysis
                    </Typography>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        variant="body_600"
                        sx={(theme: Theme) => ({
                            fontWeight: 500,
                            lineHeight: "22.5px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                            textAlign: "center",
                            width: "100%"
                        })}
                    >
                        {NO_DATA_AVAILABLE}
                    </Typography>
                </Box>
            </Box>

        )
    }



    return (
        <Box>
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
            }}>
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        lineheight: "25px",
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })}
                >
                    Schemewise Analysis
                </Typography>
            </Box >
            <Box sx={{ marginTop: "20px" }}>
                <CustomTable
                    isLoading={false}
                    tableBody={
                        <TableBodyData data={tableData?.data ?? []} />
                    }
                    tableHeaderData={tableHeaderData}
                    rowsPerPageOptions={[5, 10, 15, 20]}
                    tableData={tableData?.data ?? []}
                    handleSort={handleSort}
                    tableDataCount={tablePaginationObj.totalRows}
                    rowsPerPage={tablePaginationObj.rowsPerPage}
                    handleRowsPerPageChange={handleRowsPerPageChange}
                    handlePageChange={handlePageChange}
                    currentPage={tablePaginationObj.currentPage}
                    order={tableSortingObj.order}
                    orderBy={tableSortingObj.orderBy}
                    tableBodyLoaderRowCustomStyles={{
                        '& td:first-of-type': {
                            paddingLeft: "0px"
                        },
                    }}
                    tableHeadRowCustomStyles={{
                        '& th:first-of-type': {
                            paddingLeft: "0px"
                        }
                    }}
                    tableBodyCustomStyles={{
                        '& tr:last-child td': {
                            border: 0
                        }
                    }}
                    loadingRowsNum={5}
                    manualResponsiveBody={true}
                    hideResponsiveHeader={true}
                    ManualResponsiveBodyComponent={
                        <MobileResponsiveBodyScheme
                            // selectedHeaderKey={tableSortingObj.orderBy}
                            data={tableData?.data ?? []}
                            
                        />
                    }
                />
            </Box>
        </Box >
    )
}